import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button3D } from '@/components/ui/button-3d';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { getCachedCatalog, cacheCatalog } from '@/lib/offlineDataCache';
import type { Product } from '@/types/database';
import { groupVariants, isVariant, variantDisplayName } from '@/lib/variants';
import { unitLabel } from '@/lib/units';
import { Search, Package, Wrench, Loader2, WifiOff, Layers } from 'lucide-react';

type ItemType = 'service' | 'product';

interface CatalogItem extends Product {
  type: ItemType;
  unit: string | null;
}

interface CatalogSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (products: Product[]) => void;
}

export function CatalogSelector({ open, onOpenChange, onSelect }: CatalogSelectorProps) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { isOnline } = useNetworkStatus();
  const [products, setProducts] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<ItemType>('service');

  useEffect(() => {
    if (open && user) {
      loadProducts();
      setSelected(new Set());
      setSearch('');
    }
  }, [open, user, isOnline, activeCompany?.id]);

  const loadProducts = async () => {
    if (!user) return;
    
    setLoading(true);
    
    // Always try cache first for instant display
    const cached = getCachedCatalog(user.id);
    if (cached && cached.length > 0) {
      const mappedItems: CatalogItem[] = cached.map((item: any) => ({
        ...item,
        type: item.type || 'service',
        unit: item.unit || null,
      }));
      setProducts(mappedItems);
      setLoading(false);
    }
    
    // Only fetch from server if online
    if (isOnline && activeCompany) {
      try {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('company_id', activeCompany.id)
          .order('name');

        if (data && !error) {
          const mappedItems: CatalogItem[] = data.map((item: any) => ({
            ...item,
            type: item.type || 'service',
            unit: item.unit || null,
          }));
          setProducts(mappedItems);
          cacheCatalog(user.id, data);
        }
      } catch (e) {
        console.error('[CatalogSelector] Error fetching products:', e);
        // Keep cached data on error
      }
    }
    
    setLoading(false);
  };

  // Rótulo pelo vocabulário único (src/lib/units.ts), que também traduz os
  // códigos antigos do catálogo (`hour` → "Hora (h)").
  const getUnitLabel = (unitValue: string | null, type: ItemType) => unitLabel(unitValue, type) || null;

  // A consulta de `products` traz pais e filhas misturados. Aqui a lista vira
  // hierárquica: o PAI não é selecionável (não é vendável) — quem entra no
  // orçamento é a VARIAÇÃO, que já tem id próprio, então `quote_items.product_id`
  // aponta para o item certo sem precisar de coluna nova.
  const variantsByParent = useMemo(() => groupVariants(products), [products]);

  const filteredGroups = useMemo(() => {
    const term = search.toLowerCase().trim();
    return products
      .filter((p) => !isVariant(p) && p.type === activeTab)
      .map((parent) => ({
        parent,
        variants: (variantsByParent.get(String(parent.id)) ?? []) as CatalogItem[],
      }))
      .filter(({ parent, variants }) => {
        if (!term) return true;
        if (parent.name.toLowerCase().includes(term)) return true;
        // Casar pelo rótulo da variação também: procurar "P" tem que achar.
        return variants.some((v) => String(v.variant_name ?? '').toLowerCase().includes(term));
      });
  }, [products, activeTab, search, variantsByParent]);

  const toggleProduct = (productId: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelected(newSelected);
  };

  const handleConfirm = () => {
    // O contrato de `onSelect` NÃO muda (segue Product[]): a variação já é um
    // produto. Só compomos o nome legível — "Camiseta — P" — para o item do
    // orçamento e o PDF mostrarem exatamente o que foi escolhido.
    const selectedProducts = products
      .filter((p) => selected.has(p.id))
      .map((p) => {
        if (!isVariant(p)) return p;
        const parent = products.find((c) => c.id === p.parent_product_id);
        return { ...p, name: variantDisplayName(parent?.name ?? p.name, p.variant_name) };
      });
    onSelect(selectedProducts);
    onOpenChange(false);
    setSearch('');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  // Conta as unidades SELECIONÁVEIS: um pai com variações não é vendável, então
  // ele não entra na conta — suas variações entram.
  const parentsWithVariants = useMemo(
    () => new Set(products.filter(isVariant).map((p) => String(p.parent_product_id))),
    [products],
  );
  const sellable = (type: ItemType) =>
    products.filter((p) => p.type === type && !parentsWithVariants.has(String(p.id))).length;
  const serviceCount = sellable('service');
  const productCount = sellable('product');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Selecionar do catálogo
            {!isOnline && (
              <span className="flex items-center gap-1 text-xs text-amber-600 font-normal">
                <WifiOff className="h-3 w-3" />
                Offline
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ItemType)} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="service" className="gap-2">
              <Wrench className="h-4 w-4" />
              Serviços
              {serviceCount > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-5 text-xs">
                  {serviceCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="product" className="gap-2">
              <Package className="h-4 w-4" />
              Produtos
              {productCount > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-5 text-xs">
                  {productCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Search */}
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={`Buscar ${activeTab === 'service' ? 'serviço' : 'produto'}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>

          <TabsContent value="service" className="mt-3 flex-1 overflow-hidden">
            <ProductList
              groups={filteredGroups}
              loading={loading}
              search={search}
              selected={selected}
              toggleProduct={toggleProduct}
              formatCurrency={formatCurrency}
              getUnitLabel={getUnitLabel}
              emptyText="serviço"
              isOnline={isOnline}
            />
          </TabsContent>

          <TabsContent value="product" className="mt-3 flex-1 overflow-hidden">
            <ProductList
              groups={filteredGroups}
              loading={loading}
              search={search}
              selected={selected}
              toggleProduct={toggleProduct}
              formatCurrency={formatCurrency}
              getUnitLabel={getUnitLabel}
              emptyText="produto"
              isOnline={isOnline}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex gap-2 mt-2">
          <Button3D color="gray" onClick={() => onOpenChange(false)} className="flex-1">
            Cancelar
          </Button3D>
          <Button3D color="green" onClick={handleConfirm} disabled={selected.size === 0} className="flex-1">
            Adicionar {selected.size > 0 && `(${selected.size})`}
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductList({
  groups,
  loading,
  search,
  selected,
  toggleProduct,
  formatCurrency,
  getUnitLabel,
  emptyText,
  isOnline
}: {
  groups: { parent: CatalogItem; variants: CatalogItem[] }[];
  loading: boolean;
  search: string;
  selected: Set<string>;
  toggleProduct: (id: string) => void;
  formatCurrency: (value: number) => string;
  getUnitLabel: (unit: string | null, type: ItemType) => string | null;
  emptyText: string;
  isOnline: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
        <Package className="mb-2 h-10 w-10 opacity-50" />
        <p>{search ? `Nenhum ${emptyText} encontrado` : `Nenhum ${emptyText} no catálogo`}</p>
      </div>
    );
  }

  /** Linha selecionável — serve tanto para produto simples quanto para variação. */
  const row = (product: CatalogItem, label: string, nested: boolean) => (
    <div
      key={product.id}
      className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
        nested ? 'ml-6' : ''
      } ${
        selected.has(product.id)
          ? 'bg-primary/10 border-primary'
          : 'hover:bg-accent/50 border-border'
      }`}
      onClick={() => toggleProduct(product.id)}
    >
      <Checkbox
        checked={selected.has(product.id)}
        onCheckedChange={() => toggleProduct(product.id)}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate">{label}</p>
          {product.unit && (
            <Badge variant="outline" className="text-xs shrink-0">
              {getUnitLabel(product.unit, product.type)}
            </Badge>
          )}
        </div>
        {!nested && product.description && (
          <p className="text-xs text-muted-foreground truncate">{product.description}</p>
        )}
      </div>
      <span className="font-semibold text-primary shrink-0">
        {formatCurrency(product.price)}
      </span>
    </div>
  );

  return (
    <div className="overflow-y-auto space-y-2 max-h-[40vh] pr-1">
      {groups.map(({ parent, variants }) =>
        variants.length === 0 ? (
          row(parent, parent.name, false)
        ) : (
          // Pai com variações: cabeçalho NÃO selecionável (ele não é vendável)
          // e cada variação como linha própria.
          <div key={parent.id} className="space-y-2">
            <div className="flex items-center gap-2 px-1 pt-1 text-sm font-medium text-muted-foreground">
              <Layers className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{parent.name}</span>
            </div>
            {variants.map((variant) => row(variant, variant.variant_name || variant.name, true))}
          </div>
        ),
      )}
    </div>
  );
}
